import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useContext
} from 'react';
import { useTracking, getLabel, useId, toReactProps } from '../utils';
import { Input, HelpText, Label } from '../';
import type { TrackingProp } from '../types';
import type { InputProps } from '../input/input';
import type { HelpTextProps, LabelProps } from '../';
import { InputGroupContext } from './utils';
import styles from './input-group.module.css';
import clsx from 'clsx';

type Value = InputProps['value'];

export type InputGroupProps = Omit<
  React.ComponentPropsWithoutRef<'fieldset'>,
  'onChange'
> & {
  'data-theme'?: string;
  defaultValue?: InputProps['value'];
  disabled?: boolean;
  invalid?: boolean;
  name: string;
  onChange?: (values: string[]) => void;
  required?: boolean;
  tracking?: TrackingProp;
  type?: 'radio' | 'checkbox';
  value?: Value;
  variant?: 'row' | 'row-background' | 'column' | 'column-background';
};

export type InputGroupInputProps = Omit<InputProps, 'wrapperProps'> & {
  label: React.ReactNode | Omit<LabelProps, 'htmlFor'>;
  helpText?: React.ReactNode | Omit<HelpTextProps, 'htmlFor'>;
  wrapperProps?: React.ComponentPropsWithoutRef<'div'>;
  graphic?: React.ReactNode;
  tracking?: TrackingProp;
};

const TRACK_VALUES = '@values'; // Use input values in "event_status" tracking

const InputGroupComp = (
  {
    children,
    className,
    defaultValue,
    disabled,
    invalid,
    name,
    onChange,
    required,
    tracking = true,
    type = 'radio',
    value,
    variant = 'row',
    ...rest
  }: InputGroupProps,
  ref: React.ForwardedRef<HTMLFieldSetElement>
) => {
  // Support both value (controlled) and defaultValue (uncontrolled)
  // Read more on https://react.dev/reference/react-dom/components/input#optimizing-re-rendering-on-every-keystroke
  const [defaults, setDefaults] = useState(defaultValue);
  const values = toStrings(value ?? defaults);
  const innerRef = useRef<HTMLFieldSetElement>(null);
  const classes = clsx(styles.inputGroup, styles[variant], className);

  const track = useTracking(tracking);

  const handleLegacyChange = ({
    target: input,
    currentTarget: group
  }: React.ChangeEvent<HTMLFieldSetElement>) => {
    if (!(input instanceof HTMLInputElement) || input.name !== name) return; // Only track inputs with same name the <InputGroup>
    if (!input.closest('label')) return;

    const checked = group.querySelectorAll<HTMLInputElement>('input:checked');
    const values = Array.from(checked || [], ({ value }) => value);
    setValue(input, values);
  };

  const setValue = (input: HTMLInputElement, values: string[]) => {
    if (!(input instanceof HTMLInputElement) || input.name !== name) return; // Only track inputs with same name the <InputGroup>

    const legend = innerRef.current?.querySelector(
      'legend'
    ) as HTMLLegendElement;
    const checked =
      innerRef.current?.querySelectorAll<HTMLInputElement>('input:checked');
    const labels = Array.from(checked || [], getLabel);
    const inputValues = values.length
      ? values // comes from supporting legacy markup
      : Array.from(checked || [], ({ value }) => value);

    const status = typeof tracking === 'object' && tracking.eventStatus;
    const overwrite = {};
    setDefaults(inputValues);

    // Keep track of how many inputs have a custom eventStatus
    const customStatus = Array.from(
      checked || [],
      (input) =>
        input.dataset.trackstatus !== 'false' && input.dataset.trackstatus
    ).filter(Boolean); // Filter out falsy values from check above

    if (customStatus.length) {
      // Check for custom eventStatus, TRACK_VALUES, or nothing specified (label)
      const mixedItems = Array.from(checked || [], (input) => {
        const trackStatus = input.dataset.trackstatus;

        // Handle custom eventStatus or TRACK_VALUES
        if (trackStatus && trackStatus !== 'false')
          return trackStatus === TRACK_VALUES ? input.value : trackStatus;

        // If no custom eventStatus or TRACK_VALUES, we check parent tracking and
        // return either value or label
        return status === TRACK_VALUES ? input.value : getLabel(input);
      });

      Object.assign(overwrite, { eventStatus: mixedItems.sort().join('_') });
    }
    // If no custom eventStatus, use parent tracking as before:
    // If eventStatus is default or set to TRACK_VALUES
    else if (!status || status === TRACK_VALUES) {
      const items = status === TRACK_VALUES ? inputValues : labels;
      Object.assign(overwrite, { eventStatus: items.sort().join('_') });
    }

    track(
      {
        eventCategory: `inputgroup_${type}`,
        eventAction: input.checked ? 'checked' : 'unchecked',
        eventLabel: getLabel(legend),
        ...(tracking === true ? {} : tracking)
      },
      overwrite
    );
    onChange?.(inputValues);
  };

  const useGroupProps = (props: InputProps) => {
    if (props.name && props.name !== name) return; // Only take control if input name is missing or same as InputGroup name
    props['aria-invalid'] = props.invalid || invalid;
    props.checked = values.includes(String(props.value));
    props.name = name;
    props.onChange = props.onChange || (() => {});
    props.required = props.required || required;
    props.disabled = props.disabled || disabled;
    props.tracking = false; // Let input group do the tracking
    props.type = props.type || type;
  };

  useImperativeHandle(ref, () => innerRef.current as HTMLFieldSetElement); // Forward innerRef
  useEffect(() => {
    if (innerRef.current?.querySelector('legend')) return;
    console.warn('InputGroup is missing <Legend> element', innerRef.current);
  }, []);

  return (
    <fieldset
      {...rest}
      className={classes}
      onChange={handleLegacyChange}
      ref={innerRef}
    >
      <InputGroupContext.Provider value={{ useGroupProps, setValue, track }}>
        {children}
      </InputGroupContext.Provider>
    </fieldset>
  );
};

const InputGroupInput = (
  {
    helpText,
    label,
    graphic,
    wrapperProps,
    tracking = true,
    ...rest
  }: InputGroupInputProps,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const { useGroupProps, setValue } = useContext(InputGroupContext);
  const props = { ...rest };
  useGroupProps(props);
  const ariaId = useId();
  const inputId = rest.id || ariaId || '';
  const labelProps = toReactProps<LabelProps>(label);
  const helpTextProps = toReactProps<HelpTextProps>(helpText);

  // Use custom eventStatus if set, otherwise sets 'false'
  const trackStatus = typeof tracking === 'object' && tracking.eventStatus;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    setValue(input, []);
    if (rest.onChange) rest.onChange(event); // call the provided onChange prop if it exists
  };

  return (
    <div
      {...wrapperProps}
      ref={ref}
      className={clsx(styles.inputGroupItem, wrapperProps?.className)}
    >
      {!!graphic && <div className={styles.graphic}>{graphic}</div>}
      <div className={styles.grid}>
        <Input
          {...rest}
          id={inputId}
          type={rest.type} // Ensure type is passed down
          onChange={onChange}
          data-trackstatus={trackStatus}
        />
        <Label {...labelProps} htmlFor={inputId} />
        {!!helpText && (
          <HelpText
            {...helpTextProps}
            className={clsx(styles.helpText, helpTextProps?.className)}
            htmlFor={inputId}
          />
        )}
      </div>
    </div>
  );
};

const InputGroupComponent = forwardRef<HTMLFieldSetElement, InputGroupProps>(
  InputGroupComp
);
const InputGroupInputComp = forwardRef<HTMLDivElement, InputGroupInputProps>(
  InputGroupInput
);

export const InputGroup = Object.assign(InputGroupComponent, {
  TRACK_VALUES,
  Input: InputGroupInputComp
});

InputGroupComponent.displayName = 'InputGroup';
InputGroupInputComp.displayName = 'InputGroup.Input';

const toStrings = (value: unknown) =>
  (Array.isArray(value) ? value : [value]).map(String);
